import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { AnalyticsService } from "./../../../core/analytics/analyticsService";
import { IInvitationRequestWithTeamsAndRoles, IInvitationRequestWithTeamsAndRolesMap, IUser, UserService } from "@gtmhub/users";
import { toKeyMap } from "@gtmhub/util";
import { validEmail } from "./../../../util/email-regex";
import { IModalComponent } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IComponentOptions, IPromise, IQService, ITimeoutService } from "angular";
import { GtmhubController, ILicense } from "@gtmhub/core";
import { IInvitationRequest } from "../..";
import { CfMap } from "@gtmhub/customFields";
import { AccountService } from "@gtmhub/accounts/accountsService";
import { IAccountSubscription } from "@gtmhub/configuration";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { SnackbarEvents } from "@gtmhub/shared/components/ui/snackbar/snackbar.events";
import { ICollection } from "@gtmhub/models";
import { IIndicator } from "@gtmhub/error-handling";
import { StateService } from "@uirouter/core";

export interface IUserInvitationComponentBindings extends IModalComponent {
  close(): void;
}

export interface IEmailTag {
  id: string;
  text: string;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface UserInvitationCtrl extends IUserInvitationComponentBindings {}
export class UserInvitationCtrl extends GtmhubController {
  accountLicenses: ILicense[];
  allowUserInvitation: boolean;
  shouldFillAllForms: boolean;
  invitations: IInvitationRequestWithTeamsAndRolesMap;
  unifiedInvitation: IInvitationRequestWithTeamsAndRoles;
  emailsList: IEmailTag[];
  sendEmailInvitation: boolean;
  requiredCfNotEmpty: boolean;
  currentEmailCheckups: {
    [email: string]: {
      valid?: boolean;
      error?: string;
    };
  };
  incorrectEmails: {
    lockMultiAccount: string[];
    alreadyExists: string[];
    invalidAddresess: string[];
    hasIncorrectEmail: boolean;
  };
  indicators: {
    inviting?: IIndicator;
  };
  isTrialAccount: boolean;
  private defaultInvitation: IInvitationRequestWithTeamsAndRoles;
  private snackbarEvents: SnackbarEvents;

  static $inject = ["$timeout", "$rootScope", "$q", "$state", "UserService", "AccountService", "AccountResolverService", "AnalyticsService", "UIErrorHandlingService"];

  constructor(
    private $timeout: ITimeoutService,
    private $rootScope: ITraceRootScopeService,
    private $q: IQService,
    private $state: StateService,
    private userService: UserService,
    private accountService: AccountService,
    private accountResolverService: AccountResolverService,
    private analyticsService: AnalyticsService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    super();

    this.getAccountLicenses();
    this.allowUserInvitation = this.$rootScope.hasPermission("ManageUsers") && this.$rootScope.featureAvailable("people.invite");
    this.snackbarEvents = new SnackbarEvents(this.$rootScope);
  }

  $onInit(): void {
    this.emailsList = [];
    this.invitations = {};
    this.shouldFillAllForms = false;
    this.sendEmailInvitation = true;
    this.currentEmailCheckups = {};
    this.indicators = {
      inviting: {
        progress: false,
      },
    };
    this.setIsTrialAccount();
    this.checkForInputErrors();
  }

  removeInvitationForm(email: string): void {
    this.emailsList = this.emailsList.filter(({ text: e }) => e !== email);
    delete this.invitations[email];

    this.checkForInputErrors();
  }

  toggleFillAllForms(): void {
    this.shouldFillAllForms = !this.shouldFillAllForms;
    this.setDataToForm();
  }

  showFillAllToggle(): boolean {
    return Object.entries(this.invitations).length > 1;
  }

  shouldDisableInviteButton(): boolean {
    const requiredCfNotEmpty = this.shouldFillAllForms ? this.unifiedInvitation.requiredCfNotEmpty : this.requiredCfNotEmpty;

    return !this.invitationsLength() || this.incorrectEmails.hasIncorrectEmail || !requiredCfNotEmpty;
  }

  updateCustomFields(invitation: IInvitationRequest, customFields: CfMap): void {
    invitation.customFields = customFields;
  }

  setRequiredCf(requiredCf: { areFilled: boolean }, invitation: IInvitationRequestWithTeamsAndRoles): void {
    invitation.requiredCfNotEmpty = requiredCf.areFilled;
    this.requiredCfNotEmpty = Object.values(this.invitations).every((invitation) => invitation.requiredCfNotEmpty);
  }

  invitationsLength(): number {
    return Object.keys(this.invitations).length;
  }

  private addInvitationForm(email: string): void {
    this.invitations[email] = { ...this.defaultInvitation, email };
  }

  invite(): void {
    this.$rootScope.traceAction("send_invitation", () => {
      this.indicators = {
        inviting: { progress: true },
      };

      if (this.shouldFillAllForms) {
        this.fillInvitationsWithUnifiedInvitationData();
      }

      this.userService
        .createUsers(this.invitations, { sendEmailInvitaion: this.sendEmailInvitation })
        .then((invitedUsers: ICollection<IUser>) => {
          this.$rootScope.$broadcast("usersChanged", true);
          this.$rootScope.$broadcast("employeesChanged", true);
          this.$rootScope.$broadcast("invitedUsers", invitedUsers);
          this.snackbarEvents.broadcastShowSnackbar("create_user_success");
          this.analyticsService.track("Users Invited", { invitees_count: invitedUsers.items.length });

          const isHomeState = this.$state.current.name.includes("home");
          if (isHomeState) {
            this.$state.go("gtmhub.usersList");
          } else {
            this.close();
          }
        })
        .catch((err) => console.error(err));
    });
  }

  checkTagText(tag: string): IPromise<void> {
    if (tag.includes(" ")) {
      const uniqueTags = tag.split(" ");
      const tagsMap = uniqueTags.map((t) =>
        this.checkIfEmailIsValid(t)
          .catch((error) => {
            this.uiErrorHandlingService.handleModal(error);
          })
          .finally(() => {
            this.emailsList.push({ text: t, id: Math.random().toString() });
          })
      );

      return this.$q.all(tagsMap).then(() => {
        this.removeInvitationForm(tag);
      });
    }

    return this.checkIfEmailIsValid(tag);
  }

  checkIfEmailIsValid(currentTag: string): IPromise<void> {
    if (this.currentEmailCheckups[currentTag] && !this.currentEmailCheckups[currentTag].valid) {
      return this.$q.resolve();
    }

    this.currentEmailCheckups[currentTag] = {};

    if (this.invitations[currentTag]) {
      this.currentEmailCheckups[currentTag] = {
        valid: true,
        error: "already in list",
      };
      return this.$q.resolve();
    }

    const regExMatched = this.matchesEmailRegEx(currentTag);

    if (!regExMatched) {
      this.currentEmailCheckups[currentTag] = {
        valid: false,
        error: "invalid email syntax",
      };
      return this.$q.resolve();
    }

    return this.userService
      .validateUserEmail(currentTag)
      .then(() => {
        this.currentEmailCheckups[currentTag] = {
          valid: true,
          error: "",
        };
        this.addInvitationForm(currentTag);
      })
      .catch((error) => {
        if (error.status === 409) {
          this.currentEmailCheckups[currentTag] = {
            valid: false,
            error: error.data,
          };

          return;
        }

        this.uiErrorHandlingService.handleModal(error);
      });
  }

  setLicenseType(licenseType: string, invitation: IInvitationRequest): void {
    invitation.subscriptionType = licenseType;
  }

  removeAllInvalidEmails(): void {
    this.emailsList.forEach(({ text: email }) => !this.currentEmailCheckups[email].valid && this.removeInvitationForm(email));
  }

  normalizeInput(): void {
    /* 
      the buffer array insures if we have same email in the input more than once
      it will make it as part of the email list only once
      f.e. input: example@mail.com, example@mail.com -> buffer: 'example@mail.com': { 'text': 'example@mail.com'} ->
        -> emailList: [{'id': 'random id', 'text': 'example@mail.com'}] 
    */
    const lastEmailInList = this.emailsList[this.emailsList.length - 1];
    const bufferEmaiArr = toKeyMap("text", this.emailsList);
    this.emailsList = Object.values(bufferEmaiArr);
    lastEmailInList.id = Math.random().toString();

    if (this.currentEmailCheckups[lastEmailInList.text].error === "already in list") {
      this.$timeout(() => {
        this.currentEmailCheckups[lastEmailInList.text].error = "";
      }, 300);
    }

    this.checkForInputErrors();
  }

  private checkForInputErrors(): void {
    this.incorrectEmails = {
      lockMultiAccount: [],
      alreadyExists: [],
      invalidAddresess: [],
      hasIncorrectEmail: false,
    };

    this.emailsList.forEach(({ text: email }) => {
      if (this.currentEmailCheckups[email] && !this.currentEmailCheckups[email].valid) {
        switch (this.currentEmailCheckups[email].error) {
          case "there is already a user with this email in your account":
            this.incorrectEmails.alreadyExists.push(email);
            break;
          case "account does not allow adding multi account users":
            this.incorrectEmails.lockMultiAccount.push(email);
            break;
          case "invalid email syntax":
            this.incorrectEmails.invalidAddresess.push(email);
            break;
        }
      }
    });

    this.incorrectEmails.hasIncorrectEmail = !!(
      this.incorrectEmails.alreadyExists.length ||
      this.incorrectEmails.invalidAddresess.length ||
      this.incorrectEmails.lockMultiAccount.length
    );
  }

  selectTeamsAndAddToInvitation(invitation: IInvitationRequestWithTeamsAndRoles) {
    return (teamIds: string[]) => {
      invitation.teamIds = teamIds;
    };
  }

  selectRolesAndAddToInvitation(invitation: IInvitationRequestWithTeamsAndRoles) {
    return (rolesIds: string[]) => {
      invitation.roleIds = rolesIds;
    };
  }

  resetIndicator(indicator: keyof UserInvitationCtrl["indicators"]): void {
    delete this.indicators[indicator];
  }

  private matchesEmailRegEx = (email: string): boolean => {
    return validEmail(email);
  };

  private getAccountLicenses(): IPromise<unknown> {
    return this.accountService.getAccountSubscription().then((accountSubscription: IAccountSubscription) => {
      this.accountLicenses = accountSubscription.items;
      this.setDefaultInvitation();
    });
  }

  private setDefaultInvitation(): void {
    const defaultAccountLicense = this.accountLicenses.find((license) => license.type === "regular" || license.type === "restricted") || { type: "unchargeable" };
    this.defaultInvitation = {
      firstName: "",
      lastName: "",
      email: "",
      subscriptionType: defaultAccountLicense.type,
      teamIds: [],
      roleIds: [],
    };
  }

  private fillUnifiedInvitationWithCurrenData(): void {
    this.unifiedInvitation = {
      ...angular.copy(this.invitations[this.emailsList[0].text]),
      email: "",
    };
  }

  private fillInvitationsWithUnifiedInvitationData(): void {
    this.invitations = Object.keys(this.invitations).reduce((newInvites, email) => {
      newInvites[email] = {
        ...angular.copy(this.unifiedInvitation),
        email,
      };

      return newInvites;
    }, {});
  }

  private setDataToForm(): void {
    this.shouldFillAllForms ? this.fillUnifiedInvitationWithCurrenData() : this.fillInvitationsWithUnifiedInvitationData();
  }

  private setIsTrialAccount(): void {
    this.isTrialAccount = this.accountResolverService.getAccountData().type === 0;
  }
}

export const UserInvitationComponent: IComponentOptions = {
  controller: UserInvitationCtrl,
  templateUrl: require("./user-invitation.jade"),
  bindings: {
    close: "&",
  },
};
